import type { ReactNode } from 'react';
import { ChevronIcon } from '@/components/catalog/icons';
import { Button } from './Button';
import { PanelFooter } from './Panel';

export interface ListPagerProps {
  page: number;
  totalPages: number;
  total: number;
  firstRow: number;
  lastRow: number;
  /** The page's own page size — named in the "Show N per page" label. */
  perPage: number;
  showAll: boolean;
  loading?: boolean;
  onPage: (page: number) => void;
  onToggleShowAll: (next: boolean) => void;
  /** e.g. "companies" — appended to the summary line. */
  noun?: string;
  /** Show-all stopped at its cap; the line then reads "Showing the first N of M". */
  truncated?: boolean;
  /**
   * Replaces the computed summary line, for a page whose line says more than
   * the range (a client-side narrowing of the loaded rows, say).
   */
  summary?: ReactNode;
  className?: string;
}

/**
 * The footer under a list table: the range, Previous / Next, and the Show all
 * switch. It IS the `PanelFooter` — render it in that slot, not inside one.
 *
 * ⚠️ **In show-all mode there is no page**, so Previous, Next and the page
 * counter are not rendered rather than disabled: a disabled pager over one long
 * list is an affordance whose only outcome is nothing.
 *
 * ⚠️ **"Show all" is withheld when one page already holds everything** — the
 * same reason. "Show N per page" always renders in show-all mode, because it is
 * the only way back.
 */
export function ListPager({
  page,
  totalPages,
  total,
  firstRow,
  lastRow,
  perPage,
  showAll,
  loading = false,
  onPage,
  onToggleShowAll,
  noun,
  truncated = false,
  summary,
  className,
}: ListPagerProps) {
  const suffix = noun ? ` ${noun}` : '';
  const pages = Math.max(totalPages, 1);

  const line =
    summary ??
    (total === 0
      ? `No ${noun ?? 'rows'} to show`
      : showAll
        ? truncated
          ? `Showing the first ${lastRow} of ${total}${suffix}`
          : `Showing all ${total}${suffix}`
        : `Showing ${firstRow}–${lastRow} of ${total}${suffix}`);

  const offerShowAll = showAll || total > perPage || pages > 1;

  return (
    <PanelFooter className={className}>
      <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">{line}</p>
      <div className="flex flex-wrap items-center gap-2">
        {!showAll && (
          <>
            <span className="hidden text-xs text-fg-subtle tabular sm:inline">
              Page {page} of {pages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 sm:flex-none"
              leadingIcon={<ChevronIcon direction="left" />}
              disabled={loading || page <= 1}
              onClick={() => onPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 sm:flex-none"
              trailingIcon={<ChevronIcon direction="right" />}
              disabled={loading || page >= pages}
              onClick={() => onPage(page + 1)}
            >
              Next
            </Button>
          </>
        )}
        {offerShowAll && (
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 sm:flex-none"
            // Never disabled by `loading`: switching back mid-fetch is how a
            // reader cancels a long show-all load (the page's abort does it).
            onClick={() => onToggleShowAll(!showAll)}
          >
            {showAll ? `Show ${perPage} per page` : 'Show all'}
          </Button>
        )}
      </div>
    </PanelFooter>
  );
}
